import { supabase } from "./supabase.js";

// The Supabase boundary: maps a FLAT local games/books record to/from a unified
// `items` row, and wraps the remote calls. Media-specific fields that have no
// relational column ride in `media_extra` JSONB under their LOCAL field names, so
// fromCloudRow() is a lossless inverse. See design_documents/
// media-tracker-supabase-schema.md for the canonical schema.

const SOURCE_BY_MEDIA = {
	videogame: "igdb",
	book: "openlibrary",
	tv: "tvmaze",
	// Anime structure is TVmaze too (hybrid); MAL only enriches genres (in media_extra).
	anime: "tvmaze",
	// Movies: OMDb, keyed by the IMDb id ("tt0120737").
	movie: "omdb",
	// Comics have TWO sources (Metron issues, Open Library trades): the record's
	// own `source` wins — see sourceOf.
	comic: "metron",
};

// The external id is the source's own id, as text.
export function externalIdOf(record, mediaType) {
	if (mediaType === "comic") return String(record.external_id);
	if (mediaType === "book") return record.ol_id;
	if (mediaType === "movie") return String(record.imdb_id);
	if (mediaType === "tv" || mediaType === "anime") return String(record.tvmaze_id);
	return String(record.igdb_id);
}

// Every media type has one source except comics, whose records say which one.
export function sourceOf(mediaType, record = null) {
	if (mediaType === "comic" && record?.source) return record.source;
	return SOURCE_BY_MEDIA[mediaType];
}

// Local record → `items` row. We send our local uuid as the row id so the same
// item carries one id across devices; upsert keys on (user_id, source,
// external_id) regardless, so a mismatch can never duplicate.
export function toCloudRow(record, mediaType, userId) {
	let media_extra;
	if (mediaType === "tv") {
		media_extra = {
			summary: record.summary ?? null,
			formats: record.formats ?? [],
			note: record.note ?? null,
			premiered_year: record.premiered_year ?? null,
			// User opinions on sub-items (catalog itself is local-only, not synced).
			season_overrides: record.season_overrides ?? {},
			episode_overrides: record.episode_overrides ?? {},
		};
	} else if (mediaType === "anime") {
		media_extra = {
			summary: record.summary ?? null,
			formats: record.formats ?? [],
			note: record.note ?? null,
			premiered_year: record.premiered_year ?? null,
			// MAL genre enrichment (the reason anime is a separate category).
			mal_id: record.mal_id ?? null,
			genres: record.genres ?? [],
			themes: record.themes ?? [],
			demographics: record.demographics ?? [],
			studios: record.studios ?? [],
			mal_source: record.mal_source ?? null,
			// User opinions on sub-items (catalog itself is local-only, not synced).
			season_overrides: record.season_overrides ?? {},
			episode_overrides: record.episode_overrides ?? {},
		};
	} else if (mediaType === "comic") {
		media_extra = {
			summary: record.summary ?? null,
			formats: record.formats ?? [],
			note: record.note ?? null,
			release_year: record.release_year ?? null,
			// Comic catalog fields (spec "Data model"). `format` is what the item IS
			// (issue / tpb / hc / omnibus); `formats` above is how the user owns it.
			format: record.format ?? null,
			series: record.series ?? null,
			number: record.number ?? null,
			publisher: record.publisher ?? null,
			cover_date: record.cover_date ?? null,
			isbn: record.isbn ?? null,
			page_count: record.page_count ?? null,
			story_titles: record.story_titles ?? [],
			creators: record.creators ?? [],
			// User-curated contents of a collected edition; Metron's own list.
			collects: record.collects ?? [],
			reprints: record.reprints ?? [],
			gcd_id: record.gcd_id ?? null,
		};
	} else if (mediaType === "movie") {
		media_extra = {
			summary: record.summary ?? null,
			formats: record.formats ?? [],
			note: record.note ?? null,
			release_year: record.release_year ?? null,
			// OMDb film metadata (design_documents/movies-category.md decision 5).
			runtime: record.runtime ?? null,
			rated: record.rated ?? null,
			genres: record.genres ?? [],
			directors: record.directors ?? [],
			writers: record.writers ?? [],
			cast: record.cast ?? [],
			imdb_rating: record.imdb_rating ?? null,
		};
	} else {
		media_extra =
			mediaType === "book"
				? {
						authors: record.authors ?? [],
						subjects: record.subjects ?? [],
						summary: record.summary ?? null,
						formats: record.formats ?? [],
						note: record.note ?? null,
						published_year: record.published_year ?? null,
						owned_editions: record.owned_editions ?? [],
						cover_override_url: record.cover_override_url ?? null,
					}
				: {
					platforms: record.platforms ?? [],
					owned_on: record.owned_on ?? [],
					genres: record.genres ?? [],
					summary: record.summary ?? null,
					franchise: record.franchise ?? null,
					collection: record.collection ?? null,
					formats: record.formats ?? [],
					note: record.note ?? null,
					release_year: record.release_year ?? null,
					release_precision: record.release_precision ?? null,
					owned_editions: record.owned_editions ?? [],
					// Dedup key: the worker already collapses version_parent, so the
					// local igdb_id is itself canonical.
					canonical_id: String(record.igdb_id),
				};
	}

	// Manual corrections (utils/overrides.js): the catalog's value for each field the
	// user overrode, and the previous link after a re-link. Same for every media type;
	// only present when set, so a record without corrections round-trips exactly.
	if (record.source_values && Object.keys(record.source_values).length > 0) {
		media_extra.source_values = record.source_values;
	}
	if (record.relinked_from) media_extra.relinked_from = record.relinked_from;
	// Your own tags and review (roadmap #33, #34) — only when set.
	if (record.tags?.length) media_extra.tags = record.tags;
	if (record.review) media_extra.review = record.review;
	// Tracking depth (roadmap Batch 7) — only when set.
	if (record.progress) media_extra.progress = record.progress;
	if (record.loan) media_extra.loan = record.loan;
	if (record.price_note) media_extra.price_note = record.price_note;

	return {
		id: record.id,
		user_id: userId,
		media_type: mediaType,
		source: sourceOf(mediaType, record),
		external_id: externalIdOf(record, mediaType),
		title: record.title,
		// Games and movies carry a precise release date; books/TV/anime have just a
		// year (kept in media_extra.published_year / premiered_year).
		release_date:
			mediaType === "book" || mediaType === "tv" || mediaType === "anime"
				? null
				: (record.release_date ?? null),
		cover_url: record.cover_url ?? null,
		engagement_status: record.engagement_status,
		playthrough_count: record.playthrough_count ?? 0,
		times_finished: record.times_finished ?? null,
		date_first_completed: record.date_first_completed ?? null,
		last_engaged_date: record.last_engaged_date ?? null,
		ownership_status: record.ownership_status,
		rating: record.rating ?? null,
		awards: record.awards ?? [],
		visibility: record.visibility ?? "friends",
		media_extra,
		created_at: record.created_at,
		updated_at: record.updated_at,
	};
}

// `items` row → FLAT local record matching the addGame/addBook shape. Marked
// `_synced` so the refresh reconciler knows this record is cloud-backed (and may
// therefore be removed locally if it later disappears from the cloud).
export function fromCloudRow(row) {
	const extra = row.media_extra ?? {};
	const common = {
		id: row.id,
		title: row.title,
		cover_url: row.cover_url ?? null,
		engagement_status: row.engagement_status,
		ownership_status: row.ownership_status,
		rating: row.rating ?? null,
		awards: row.awards ?? [],
		formats: extra.formats ?? [],
		note: extra.note ?? null,
		summary: extra.summary ?? null,
		times_finished: row.times_finished ?? null,
		playthrough_count: row.playthrough_count ?? 0,
		date_first_completed: row.date_first_completed ?? null,
		last_engaged_date: row.last_engaged_date ?? null,
		visibility: row.visibility ?? "friends",
		...(extra.source_values ? { source_values: extra.source_values } : {}),
		...(extra.relinked_from ? { relinked_from: extra.relinked_from } : {}),
		...(extra.tags?.length ? { tags: extra.tags } : {}),
		...(extra.review ? { review: extra.review } : {}),
		...(extra.progress ? { progress: extra.progress } : {}),
		...(extra.loan ? { loan: extra.loan } : {}),
		...(extra.price_note ? { price_note: extra.price_note } : {}),
		created_at: row.created_at,
		updated_at: row.updated_at,
		_synced: true,
	};

	if (row.media_type === "book") {
		return {
			...common,
			ol_id: row.external_id,
			media_type: "book",
			authors: extra.authors ?? [],
			subjects: extra.subjects ?? [],
			published_year: extra.published_year ?? null,
			owned_editions: extra.owned_editions ?? [],
			cover_override_url: extra.cover_override_url ?? null,
		};
	}
	if (row.media_type === "tv") {
		return {
			...common,
			tvmaze_id: Number(row.external_id),
			media_type: "tv",
			premiered_year: extra.premiered_year ?? null,
			season_overrides: extra.season_overrides ?? {},
			episode_overrides: extra.episode_overrides ?? {},
		};
	}
	if (row.media_type === "anime") {
		return {
			...common,
			tvmaze_id: Number(row.external_id),
			media_type: "anime",
			premiered_year: extra.premiered_year ?? null,
			mal_id: extra.mal_id ?? null,
			genres: extra.genres ?? [],
			themes: extra.themes ?? [],
			demographics: extra.demographics ?? [],
			studios: extra.studios ?? [],
			mal_source: extra.mal_source ?? null,
			season_overrides: extra.season_overrides ?? {},
			episode_overrides: extra.episode_overrides ?? {},
		};
	}
	if (row.media_type === "comic") {
		return {
			...common,
			comic_key: `${row.source}:${row.external_id}`,
			source: row.source,
			external_id: row.external_id,
			media_type: "comic",
			release_year: extra.release_year ?? null,
			format: extra.format ?? null,
			series: extra.series ?? null,
			number: extra.number ?? null,
			publisher: extra.publisher ?? null,
			cover_date: extra.cover_date ?? null,
			isbn: extra.isbn ?? null,
			page_count: extra.page_count ?? null,
			story_titles: extra.story_titles ?? [],
			creators: extra.creators ?? [],
			collects: extra.collects ?? [],
			reprints: extra.reprints ?? [],
			gcd_id: extra.gcd_id ?? null,
		};
	}
	if (row.media_type === "movie") {
		return {
			...common,
			imdb_id: row.external_id,
			media_type: "movie",
			release_year: extra.release_year ?? null,
			release_date: row.release_date ?? null,
			runtime: extra.runtime ?? null,
			rated: extra.rated ?? null,
			genres: extra.genres ?? [],
			directors: extra.directors ?? [],
			writers: extra.writers ?? [],
			cast: extra.cast ?? [],
			imdb_rating: extra.imdb_rating ?? null,
		};
	}
	return {
		...common,
		igdb_id: Number(row.external_id),
		media_type: "videogame",
		release_year: extra.release_year ?? null,
		release_date: row.release_date ?? null,
		// Only when known, so records without it round-trip exactly.
		...(extra.release_precision ? { release_precision: extra.release_precision } : {}),
		platforms: extra.platforms ?? [],
		owned_on: extra.owned_on ?? [],
		genres: extra.genres ?? [],
		franchise: extra.franchise ?? null,
		collection: extra.collection ?? null,
		owned_editions: extra.owned_editions ?? [],
	};
}

// ---------- remote calls ----------

export async function upsertCloudRow(row) {
	const { error } = await supabase
		.from("items")
		.upsert(row, { onConflict: "user_id,source,external_id" });
	if (error) throw error;
}

// Batch variant for the one-time local→cloud migration: upsert many rows in a
// single request, same conflict target.
export async function upsertCloudRows(rows) {
	if (!rows.length) return;
	const { error } = await supabase
		.from("items")
		.upsert(rows, { onConflict: "user_id,source,external_id" });
	if (error) throw error;
}

export async function deleteCloudItem(userId, source, externalId) {
	const { error } = await supabase
		.from("items")
		.delete()
		.match({ user_id: userId, source, external_id: externalId });
	if (error) throw error;
}

export async function fetchCloudItems(userId) {
	const { data, error } = await supabase
		.from("items")
		.select("*")
		.eq("user_id", userId);
	if (error) throw error;
	return data ?? [];
}
